import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import exportIcon from "../../../assets/icons/upload.svg";
import ExportModal from "../../../components/ImpExpModal/ExportModal";
import Loader from "../../../components/Loader";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const TransactionDashboard = ({ isSidebarCollapsed, onCallApi, exportCsvData, selectedData, queryParams, filterData, from }) => {
    const [config] = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [exportType, setExportType] = useState("deal");


    // Helper function to clean currency strings
    const cleanCurrency = (value) => {
        if (!value) return 0;
        return String(value).replace(/[^0-9.-]+/g, "");
    };

    const fetchDashboardData = () => {
        const url = `${BASE_URL}/transaction-dashboard?${queryParams}`;

        axios
            .get(url, config)
            .then((res) => {
                setDashboardData(res?.data);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                if (err.response?.data?.message &&
                    err.response.data.message !== "Too Many Attempts.") {
                    NotificationManager.error(err.response.data.message);
                }
            });
    };

    useEffect(() => {
        fetchDashboardData();
    }, [queryParams]);

    if (loading) return <Loader />;

    const raw = dashboardData || {};

    const data = {
        current_loi_pipeline: {
            amount: parseInt(cleanCurrency(raw.transaction_dashboard?.current_loi_pipeline?.gci) || 0),
            deals: raw.transaction_dashboard?.current_loi_pipeline?.count || 0,
        },
        current_hopper: {
            amount: parseInt(cleanCurrency(raw.transaction_dashboard?.current_hopper?.gci) || 0),
            deals: raw.transaction_dashboard?.current_hopper?.count || 0,
        },
        expected_closings: {
            "30 Days": parseInt(cleanCurrency(raw.transaction_dashboard?.expected_closings?.["30_days"]?.gci) || 0),
            "60 Days": parseInt(cleanCurrency(raw.transaction_dashboard?.expected_closings?.["60_days"]?.gci) || 0),
            "90 Days": parseInt(cleanCurrency(raw.transaction_dashboard?.expected_closings?.["90_days"]?.gci) || 0),
        },
        hopper_split: {
            company: parseInt(cleanCurrency(raw.transaction_dashboard?.hopper_split?.company) || 0),
            broker: parseInt(cleanCurrency(raw.transaction_dashboard?.hopper_split?.broker) || 0),
        },
        this_week: {
            loiAdded: {
                amount: parseInt(cleanCurrency(raw.transaction_dashboard?.this_week?.loi_added?.gci) || 0),
                deals: raw.transaction_dashboard?.this_week?.loi_added?.count || 0,
            },
            hopperGain: {
                amount: parseInt(cleanCurrency(raw.transaction_dashboard?.this_week?.hopper_gain?.gci) || 0),
                deals: raw.transaction_dashboard?.this_week?.hopper_gain?.count || 0,
            },
            closed: {
                amount: parseInt(cleanCurrency(raw.transaction_dashboard?.this_week?.closed?.gci) || 0),
                deals: raw.transaction_dashboard?.this_week?.closed?.count || 0,
            },
            lost: {
                amount: parseInt(cleanCurrency(raw.transaction_dashboard?.this_week?.lost?.gci) || 0),
                deals: raw.transaction_dashboard?.this_week?.lost?.count || 0,
            },
            netHopperChange: {
                amount: parseInt(cleanCurrency(raw.transaction_dashboard?.this_week?.net_hopper_change?.gci) || 0),
                deals: raw.transaction_dashboard?.this_week?.net_hopper_change?.count || 0,
            },
        },
        deals: raw.deal || [],
        advisors: raw.advisor_leaderboard || [],
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    const handleExport = (type) => {
        setExportType(type);
        setShowModal(true);
    };

    const {
        current_loi_pipeline,
        current_hopper,
        expected_closings,
        hopper_split,
        this_week = {},
        deals = [],
        advisors = []
    } = data;

    const getStatusColor = (status) => {
        if (!status) return "text-gray-500";
        const s = status.toLowerCase();
        if (s.includes("executed loi") || s.includes("loi")) return "text-blue-600";
        if (s.includes("under contract") || s.includes("contract")) return "text-orange-500";
        if (s.includes("closed")) return "text-green-600";
        if (s.includes("lost")) return "text-gray-500";
        return "text-orange-500";
    };

    const getPropertyDisplay = (deal) => {
        const name = deal.property?.name || deal.name || "—";
        const addr = deal.property?.address;

        let street = "";
        let cityStateZip = "";

        if (addr) {
            street = addr.street_address || "";
            const city = addr.city || "";
            const state = addr.state || "";
            cityStateZip = [city, state].filter(Boolean).join(", ");
        }

        return (
            <div className="py-2 leading-tight">
                <div className="font-medium">{name}</div>
                {street && <div className="text-gray-500 text-sm">{street}</div>}
                {cityStateZip && <div className="text-gray-500 text-sm">{cityStateZip}</div>}
            </div>
        );
    };

    return (
        <div className="light-bg-L pb-3 px-4 py-4">
            <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} light-bg-L`}>

                {/* Top Cards & This Week Section (unchanged) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                    {/* ... Your existing top 4 cards ... */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600">Current LOI Pipeline</p>
                        <p className="head-2 dark-H mt-2">{formatCurrency(current_loi_pipeline.amount)}</p>
                        <p className="body-S text-gray-500 mt-1">{current_loi_pipeline.deals} deals</p>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600">Current Hopper</p>
                        <p className="head-2 dark-H mt-2">{formatCurrency(current_hopper.amount)}</p>
                        <p className="body-S text-gray-500 mt-1">{current_hopper.deals} deals</p>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600 mb-4">Expected Closings</p>
                        {Object.entries(expected_closings).map(([period, amount]) => (
                            <div key={period} className="flex justify-between py-2 border-b last:border-0">
                                <span className="body-S">{period}</span>
                                <span className="body-S font-semibold text-green-600">{formatCurrency(amount)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <p className="body-N text-gray-600 mb-4">Hopper Split (50/50)</p>
                        <div className="space-y-4">
                            <div className="flex justify-between"><span className="body-S">Company</span><span className="body-S font-medium">{formatCurrency(hopper_split.company)}</span></div>
                            <div className="flex justify-between"><span className="body-S">Broker</span><span className="body-S font-medium">{formatCurrency(hopper_split.broker)}</span></div>
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    <p className="head-4 dark-H mb-4">THIS WEEK</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                            { label: "LOI Added", ...this_week.loiAdded },
                            { label: "Hopper Gain", ...this_week.hopperGain },
                            { label: "Closed", ...this_week.closed, color: "text-green-600" },
                            { label: "Lost", ...this_week.lost, color: "text-gray-500" },
                            { label: "Net Hopper Change", ...this_week.netHopperChange, color: "text-green-600" },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-5 rounded-xl border border-gray-100">
                                <p className="body-N text-gray-600">{item.label}</p>
                                <p className={`head-4 mt-2 ${item.color || "dark-H"}`}>{formatCurrency(item.amount)}</p>
                                <p className="body-S text-gray-500 mt-1">{item.deals} deals</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Deals + Advisor Tables */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Deals Table */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <p className="head-4 dark-H">Deals</p>
                            <div className="flex items-center cursor-pointer" onClick={() => handleExport("deal")}>
                                <img src={exportIcon} alt="" className="mr-2" />
                                <p className="text-green-H body-S">Export</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border overflow-x-auto max-h-[400px] scrollbar-thin">
                            <table className="w-full min-w-[800px] border-collapse">
                                <thead className="sticky top-0 z-[9998] bg-[#d1dbcf]">
                                    <tr className="text-left uppercase body-N dark-M">
                                        <th className="p-4 sticky left-0 bg-[#d1dbcf] z-[9999]">Property Name</th>
                                        <th className="p-4">Advisor</th>
                                        <th className="p-4">Acquisition</th>
                                        <th className="p-4">Disposition</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4">Executed LOI Date</th>
                                        <th className="p-4">Contract Date</th>
                                        <th className="p-4">Closed / Expected Date</th>
                                        <th className="p-4">GCI to Company</th>
                                        <th className="p-4">Contract Price</th>
                                    </tr>
                                </thead>
                                <tbody className="body-N dark-M">
                                    {deals.length > 0 ? deals.map((deal, idx) => (
                                        <tr key={idx} className="border-t hover:bg-gray-50">
                                            <td className="sticky left-0 bg-white z-10 pt-2">
                                                {getPropertyDisplay(deal)}
                                            </td>
                                            <td className="body-N dark-H pt-2">
                                                {deal.advisors && deal.advisors.length > 0 ? (
                                                    deal.advisors.map((advisor, index) => (
                                                        <div key={advisor.id || index}>
                                                            {advisor.name}
                                                            {advisor.type && (
                                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {' '}
                                                                    ({advisor.type === 'buyer_broker' ? 'Buyer' : 'Seller'})
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    deal.primary_advisor || "—"
                                                )}
                                            </td>
                                            <td className="body-N dark-H pt-2">{deal.acquisition_name || "—"}</td>
                                            <td className="body-N dark-H pt-2">{deal.disposition_name || "—"}</td>
                                            <td className={`body-N p-4 font-medium ${getStatusColor(deal.status)}`}>
                                                {deal.status || "—"}
                                            </td>
                                            <td className="body-N dark-H pt-2">{deal.executed_loi_date || "—"}</td>
                                            <td className="body-N dark-H pt-2">{deal.contract_date || "—"}</td>
                                            <td className="body-N dark-H pt-2">{deal.closed_date || deal.expected_close_date || "—"}</td>
                                            <td className="body-N dark-H pt-2 text-green-600 font-medium">
                                                {deal.gci_to_company || "—"}
                                            </td>
                                            <td className="body-N dark-H p-4">
                                                {deal.contract_price || "—"}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td className="p-10 text-center text-gray-500">No deals available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Advisor Leaderboard Table */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <p className="head-4 dark-H">Advisor Pipeline (Leaderboard)</p>
                            <div className="flex items-center cursor-pointer" onClick={() => handleExport("advisor")}>
                                <img src={exportIcon} alt="" className="mr-2" />
                                <p className="text-green-H body-S">Export</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border overflow-x-auto max-h-[400px] scrollbar-thin">
                            <table className="w-full min-w-[800px] border-collapse">
                                <thead className="sticky top-0 z-[9998] bg-[#d1dbcf]">
                                    <tr className="text-left uppercase body-N dark-M">
                                        <th className="p-4 sticky left-0 bg-[#d1dbcf] z-[9999]">Advisor</th>
                                        <th className="p-4">LOI Pipeline</th>
                                        <th className="p-4">Hopper (UC)</th>
                                        <th className="p-4">Next 90 Days</th>
                                        <th className="p-4">Closed YTD</th>
                                        <th className="p-4">GCI to <br />Company<br /> YTD</th>
                                    </tr>
                                </thead>
                                <tbody className="body-N dark-M">
                                    {advisors.length > 0 ? advisors.map((advisor, idx) => (
                                        <tr key={idx} className="border-t hover:bg-gray-50">
                                            <td className="sticky left-0 bg-white z-10 pt-2 body-N dark-H">
                                                {Array.isArray(advisor.advisor)
                                                    ? advisor.advisor.map((name, i) => (
                                                        <span key={i} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded mr-1 last:mr-0">
                                                            {name}
                                                        </span>
                                                    ))
                                                    : (advisor.advisor || "—")
                                                }
                                            </td>
                                            <td className="body-N dark-H pt-2">{(advisor.loi_pipeline || "—")}</td>
                                            <td className="body-N dark-H pt-2">{(advisor.hopper || "—")}</td>
                                            <td className="body-N dark-H pt-2">{(advisor.next_90_days || "—")}</td>
                                            <td className="body-N dark-H pt-2">{(advisor.closed_ytd || "—")}</td>
                                            <td className="body-N dark-H pt-2">{(advisor.gci_to_company || "—")}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td className="p-10 text-center text-gray-500">No advisor data available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <ExportModal
                    showModal={showModal}
                    selectedData={selectedData}
                    exportCsvData={exportCsvData}
                    from={from}
                    type={exportType}
                    onCallApi={onCallApi}
                    filterData={filterData}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default TransactionDashboard;